import { LOCALES, type Locale } from "@shelflist/shared";

export const DEFAULT_LANGUAGE: Locale = "en";
export const SUPPORTED_LANGUAGES = LOCALES;

/** Native names; intentionally not translated. */
export const LANGUAGE_NAMES: Record<Locale, string> = {
  de: "Deutsch",
  en: "English",
};

const STORAGE_KEY = "shelflist.language";

export function isSupportedLanguage(value: unknown): value is Locale {
  return typeof value === "string" && (SUPPORTED_LANGUAGES as readonly string[]).includes(value);
}

/** First supported language in the browser's preference list (`de-AT` → `de`). */
export function detectBrowserLanguage(
  languages: readonly string[] = typeof navigator === "undefined" ? [] : navigator.languages,
): Locale | undefined {
  for (const tag of languages) {
    const primary = tag.toLowerCase().split("-")[0];
    if (isSupportedLanguage(primary)) return primary;
  }
  return undefined;
}

/**
 * Resolution order: the signed-in user's saved preference, then the language
 * picked on a pre-login page in this browser, then the browser languages, then English.
 */
export function resolveLanguage(input: {
  userLocale?: Locale | null;
  browserOverride?: Locale | null;
  browserLanguages?: readonly string[];
}): Locale {
  return (
    input.userLocale ??
    input.browserOverride ??
    detectBrowserLanguage(input.browserLanguages) ??
    DEFAULT_LANGUAGE
  );
}

/** Language chosen with the pre-login switcher; stored in this browser only. */
export function getBrowserOverride(): Locale | null {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return isSupportedLanguage(value) ? value : null;
  } catch {
    return null;
  }
}

export function setBrowserOverride(language: Locale | null): void {
  try {
    if (language) localStorage.setItem(STORAGE_KEY, language);
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage may be unavailable (private mode); the choice then lasts for this page view.
  }
}
